import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SecondaryActionButton } from '@aztec-realms/ui';
import { loadPushState } from './data/push-api';
import { pushCapability } from './data/push-installation';

const KEY = 'aztec-realms:push-explanation-dismissed-v1';
function wasDismissed(): boolean {
  try {
    return localStorage.getItem(KEY) === 'true';
  } catch {
    return true;
  }
}

/** Mounted only inside an open, authoritative, nonempty earned inbox. Never on login/install. */
export function PushValuePrompt({ ownerId, onReview }: { ownerId: string; onReview: () => void }) {
  const [dismissed, setDismissed] = useState(wasDismissed);
  const capability = pushCapability();
  if (dismissed || capability !== 'default') return null;
  return (
    <AvailablePrompt ownerId={ownerId} onReview={onReview} onDismiss={() => setDismissed(true)} />
  );
}

function AvailablePrompt({
  ownerId,
  onReview,
  onDismiss,
}: {
  ownerId: string;
  onReview: () => void;
  onDismiss: () => void;
}) {
  const state = useQuery({
    queryKey: ['push-settings', ownerId],
    queryFn: ({ signal }) => loadPushState(signal),
    retry: false,
    gcTime: 0,
  });
  if (!state.data?.available || state.data.enabled) return null;
  const dismiss = () => {
    onDismiss();
    try {
      localStorage.setItem(KEY, 'true');
    } catch {
      /* Remains dismissed for this visit. */
    }
  };
  return (
    <aside aria-label="Optional thank-you notifications">
      <p>
        Would you like to know when your Tribe thanks you? Device alerts are optional, with quiet
        hours you control.
      </p>
      <div className="earned-inbox__actions">
        <SecondaryActionButton
          onClick={() => {
            dismiss();
            onReview();
          }}
        >
          Review notification settings
        </SecondaryActionButton>
        <SecondaryActionButton onClick={dismiss}>Not now</SecondaryActionButton>
      </div>
    </aside>
  );
}
